"use client";

import { IconArrowBackUp } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useQueryState } from "nuqs";
import { useEffect, useRef, useState } from "react";
import { useResetPage } from "@/features/items/hooks/usePage";
import { cn } from "@/lib/cn";
import { Filter } from "./Filter";
import { useFilters } from "../useFacets";
import { useSelectedFilters } from "../useSelectedFilters";

/** One row of pills; taller than this and the bar offers to expand. */
const ONE_ROW_PX = 60;

/**
 * The row of filter pills over the map.
 *
 * Wraps to as many rows as it needs but shows only the first, with a chevron to
 * reveal the rest — a project with a dozen filters would otherwise cover the map
 * it is filtering.
 */
export function FilterBar({ isCompact }: { isCompact: boolean }) {
  const t = useTranslations();
  const { filters, isPending } = useFilters();
  const { selectedFilters, resetAll } = useSelectedFilters();
  const resetPage = useResetPage();

  // `hiddenFilters` is an embedder's parameter: a site embedding the map in an
  // iframe pins some filters and hides their controls.
  const [hidden] = useQueryState("hiddenFilters");
  const hiddenNames = hidden ? hidden.split(",") : [];

  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [isExpanded, setExpanded] = useState(false);
  const [isOverflowing, setOverflowing] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = barRef.current;
    if (!element) return;

    const measure = () => setOverflowing(element.scrollHeight > ONE_ROW_PX);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [filters]);

  // A pill's dropdown is `position: fixed`, so it doesn't close by scrolling out
  // of the way — a click anywhere else has to close it.
  useEffect(() => {
    if (!openFilter) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node))
        setOpenFilter(null);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [openFilter]);

  const visible = filters.filter(
    (filter) => !hiddenNames.includes(filter.def.name)
  );
  if (isPending || visible.length === 0) return null;

  const hasSelection = Object.keys(selectedFilters).length > 0;

  return (
    <div className="relative" ref={containerRef}>
      <div
        ref={barRef}
        className={cn(
          "filters-container flex flex-col tablet:flex-row flex-wrap gap-4 py-3 pl-3 pr-1",
          isExpanded ? "max-h-none" : "tablet:max-h-[3.5rem] overflow-hidden"
        )}
      >
        {visible.map((filter) => (
          <Filter
            key={filter.def.name}
            filter={filter}
            isCompact={isCompact}
            isOpen={openFilter === filter.def.name}
            setOpen={(open) => {
              setOpenFilter(open ? filter.def.name : null);
              // Any change of filter changes what page 3 even means.
              if (!open) resetPage();
            }}
          />
        ))}

        <div className="relative min-w-[43px] text-left hidden tablet:inline-block">
          <button
            type="button"
            onClick={() => {
              resetAll();
              resetPage();
            }}
            disabled={!hasSelection}
            className={cn(
              "focus-element group flex h-8 w-full transform items-center justify-between space-x-2",
              "rounded-full bg-white py-2 pl-2 pr-4 text-xs font-medium ring-1 ring-primary",
              "custom-hover transition-transform duration-300 hover:dynamic-text focus:outline-none",
              "dark:bg-gray-800 dark:text-off-white dark:ring-primary",
              "disabled:pointer-events-none disabled:opacity-50 disabled:ring-gray-300"
            )}
          >
            <IconArrowBackUp size={16} aria-hidden />
            {!isCompact && (
              <span className="flex-grow text-left">
                {t("search.filters.clear")}
              </span>
            )}
          </button>
        </div>
      </div>

      {isOverflowing && (
        <span className="absolute w-full items-center justify-center hidden tablet:flex">
          <button
            type="button"
            onClick={() => {
              setOpenFilter(null);
              setExpanded((current) => !current);
            }}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? t("search.seeLess") : t("search.seeMore")}
            className="rounded-b bg-gray-50 bg-opacity-40 px-3 backdrop-blur dark:bg-dark-color dark:bg-opacity-20"
          >
            <i
              className={cn(
                "icon cursor-pointer",
                isExpanded
                  ? "ic-material_ic_keyboard_arrow_up_48px"
                  : "ic-material_ic_keyboard_arrow_down_48px"
              )}
              aria-hidden
            />
          </button>
        </span>
      )}
    </div>
  );
}
